import { useQuery } from '@tanstack/react-query'
import { BookOpen, CalendarDays, CalendarPlus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router'
import { ErrorState, LoadingState } from '@/components/feedback/States'
import { Button } from '@/components/ui/Button'
import { PageHeader } from '@/components/ui/PageHeader'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { runsQuery } from '@/features/courses/api'
import { RunDialog } from '@/features/courses/components/RunDialog'
import { RunList } from '@/features/courses/components/RunList'
import { STATUS_LABEL } from '@/features/courses/status'
import type { RunStatus } from '@/features/courses/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

/** Every scheduled course (runs), by status. */
export function CourseRunsPage() {
  const { t } = useTranslation('courses')
  useDocumentTitle('Scheduled courses')
  const [params, setParams] = useSearchParams()
  const status = (params.get('status') ?? 'running') as RunStatus | 'all'
  const runs = useQuery(runsQuery(status === 'all' ? {} : { status }))
  const [scheduling, setScheduling] = useState(false)

  return (
    <>
      <PageHeader
        title={t('Scheduled courses')}
        description={t('Each group follows a course between its own dates, with its own weekly sessions.')}
        breadcrumbs={[{ label: t('Courses') }, { label: t('Scheduled courses') }]}
        homeHref="/admin"
        actions={
          <>
            <Button variant="secondary" leftIcon={<BookOpen />} asChild>
              <Link to="/admin/courses">{t('Catalogue')}</Link>
            </Button>
            <Button variant="secondary" leftIcon={<CalendarDays />} asChild>
              <Link to="/admin/courses/calendar">{t('Calendar')}</Link>
            </Button>
            <Button leftIcon={<CalendarPlus />} onClick={() => setScheduling(true)}>
              {t('Schedule a course')}
            </Button>
          </>
        }
      />
      <Tabs
        value={status}
        onValueChange={(v) => setParams({ status: v }, { replace: true })}
        className="mb-4"
      >
        <TabsList aria-label={t('Status')}>
          {(['running', 'upcoming', 'finished', 'all'] as const).map((s) => (
            <TabsTrigger key={s} value={s}>
              {s === 'all' ? t('All') : t(STATUS_LABEL[s])}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {runs.isPending ? (
        <LoadingState />
      ) : runs.error ? (
        <ErrorState onRetry={() => void runs.refetch()} />
      ) : (
        <RunList runs={runs.data} emptyText={t('No scheduled courses here.')} />
      )}
      <RunDialog open={scheduling} onOpenChange={setScheduling} />
    </>
  )
}
